import { Main } from "@components/Main";
import { PageContent } from "@components/PageContent";
import { PageTitle, PageTitleText } from "@components/PageTitle";
import { useData } from "@hooks/useData";
import { useEffectOnce } from "@hooks/useEffectOnce";
import { useField } from "@hooks/useField";
import { useFilter } from "@hooks/useFilter";
import useLoading from "@hooks/useLoading";
import useMessage from "@hooks/useMessage";
import { useValueState } from "@hooks/useValueState";
import { useContratoOperadoraCombo } from "@model/ContratoOperadora";
import ILinha from "@model/Linha";
import { useLinhaService } from "@services/LinhaService";
import { IRelatorioStatusLinhaFilter, IRelatorioStatusLinhaSummary, useRelatorioService } from "@services/RelatorioService";
import { noop } from "@utils/Functions";
import React from "react";
import { RelatorioStatusLinhaCards } from "./Cards";
import { RelatorioStatusLinhaFilter } from "./Filter";
import { RelatorioStatusLinhaTabs } from "./Tabs";

export type RelatorioStatusLinhaProps = {
}

export function RelatorioStatusLinha (props: RelatorioStatusLinhaProps) {
    const numerosContrato = useValueState<string[]>();

    const filter = useFilter<IRelatorioStatusLinhaFilter>({
        ddd: useField(),
        telefone: useField(),
        contratoOperadoraId: useField(),
        numerosContrato: useField(),
    })

    const clienteData = useData<ILinha, IRelatorioStatusLinhaFilter>({
        filter: {
            ddd: useField(),
            telefone: useField(),
            contratoOperadoraId: useField(),
            numerosContrato: useField(),
        }
    })

    const funcionarioData = useData<ILinha, IRelatorioStatusLinhaFilter>({
        filter: {
            ddd: useField(),
            telefone: useField(),
            contratoOperadoraId: useField(),
            numerosContrato: useField(),
        }
    })

    const semContratoData = useData<ILinha, IRelatorioStatusLinhaFilter>({
        filter: {
            ddd: useField(),
            telefone: useField(),
            contratoOperadoraId: useField(),
            numerosContrato: useField(),
        }
    })

    const clienteLoading = useLoading();
    const funcionarioLoading = useLoading();
    const semContratoLoading = useLoading();
    
    const clienteDataSummary = useValueState<IRelatorioStatusLinhaSummary>();
    const funcionarioDataSummary = useValueState<IRelatorioStatusLinhaSummary>();
    const semContratoDataSummary = useValueState<IRelatorioStatusLinhaSummary>();

    const message = useMessage();
    const relatorioService = useRelatorioService();
    const linhaService = useLinhaService();

    const contratoOperadoraCombo = useContratoOperadoraCombo();

    const handleClienteCsv = React.useCallback(() => {
        if (!clienteLoading.isLoading) {
            clienteLoading.start();

            const request = {
                ...clienteData.requestedData,
                filter: filter.getInputValues()
            }

            relatorioService.exportarStatusLinhaCliente(request)
                .catch(message.showBlobException)
                .finally(clienteLoading.stop);
        }
    }, [clienteData.requestedData, clienteLoading, filter, message.showBlobException, relatorioService]);

    const handleFuncionarioCsv = React.useCallback(() => {
        if (!funcionarioLoading.isLoading) {
            funcionarioLoading.start();

            const request = {
                ...funcionarioData.requestedData,
                filter: filter.getInputValues()
            }

            relatorioService.exportarStatusLinhaFuncionario(request)
                .catch(message.showBlobException)
                .finally(funcionarioLoading.stop);
        }
    }, [funcionarioData.requestedData, funcionarioLoading, filter, message.showBlobException, relatorioService]);

    const handleSemContratoCsv = React.useCallback(() => {
        if (!semContratoLoading.isLoading) {
            semContratoLoading.start();

            const request = {
                ...semContratoData.requestedData,
                filter: filter.getInputValues()
            }

            relatorioService.exportarStatusLinhaSemContrato(request)
                .catch(message.showBlobException)
                .finally(semContratoLoading.stop);
        }
    }, [semContratoData.requestedData, semContratoLoading, filter, message.showBlobException, relatorioService]);

    React.useEffect(() => {
        if (!clienteLoading.isLoading) {
            clienteLoading.start();

            const request = {
                ...clienteData.requestedData,
                filter: filter.getInputValues()
            }

            relatorioService.listarStatusLinhaCliente(request)
                .then(response => {
                    const data = response.data;

                    clienteData.updateDataResponse(data.data);
                    clienteDataSummary.set(data.summary);
                })
                .catch(message.showException)
                .finally(clienteLoading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteData.requestedData]);

    React.useEffect(() => {
        if (!funcionarioLoading.isLoading) {
            funcionarioLoading.start();

            const request = {
                ...funcionarioData.requestedData,
                filter: filter.getInputValues()
            }

            relatorioService.listarStatusLinhaFuncionario(request)
            .then(response => {
                const data = response.data;

                funcionarioData.updateDataResponse(data.data);
                funcionarioDataSummary.set(data.summary);
            })
            .catch(message.showException)
                .finally(funcionarioLoading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [funcionarioData.requestedData]);

    React.useEffect(() => {
        if (!semContratoLoading.isLoading) {
            semContratoLoading.start();

            const request = {
                ...semContratoData.requestedData,
                filter: filter.getInputValues()
            }

            relatorioService.listarStatusLinhaSemContrato(request)
                .then(response => {
                    const data = response.data;

                    semContratoData.updateDataResponse(data.data);
                    semContratoDataSummary.set(data.summary);
                })
                .catch(message.showException)
                .finally(semContratoLoading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [semContratoData.requestedData]);

    const atualizarViaFiltro = React.useCallback(() => {
        clienteData.filter.handleFilterRequest();
        funcionarioData.filter.handleFilterRequest();
        semContratoData.filter.handleFilterRequest();
        
    }, [clienteData.filter, funcionarioData.filter, semContratoData.filter]);
    
    useEffectOnce(() => {
        contratoOperadoraCombo.loadOptions();
        linhaService.listarNumerosContrato()
            .then(numerosContrato.handleResponse)
            .catch(noop);
    });

    return (
        <Main>
            <PageTitle>
                <PageTitleText text="Relatório de Status de Linhas" />
            </PageTitle>

            <PageContent>
                <RelatorioStatusLinhaFilter
                    filter={filter}
                    isLoading={clienteLoading.isLoading || funcionarioLoading.isLoading || semContratoLoading.isLoading}
                    contratoOperadoraCombo={contratoOperadoraCombo}
                    numerosContrato={numerosContrato}
                    onSearch={atualizarViaFiltro}
                />
                
                <RelatorioStatusLinhaCards 
                    cliente={clienteDataSummary}
                    funcionario={funcionarioDataSummary}
                    semContrato={semContratoDataSummary}

                    isClienteLoadingSummary={clienteLoading.isLoading}
                    isFuncionarioLoadingSummary={funcionarioLoading.isLoading}
                    isSemContratoLoadingSummary={semContratoLoading.isLoading}
                />

                <RelatorioStatusLinhaTabs 
                    clienteData={clienteData}
                    funcionarioData={funcionarioData}
                    semContratoData={semContratoData}
                    clienteLoading={clienteLoading}
                    funcionarioLoading={funcionarioLoading}
                    semContratoLoading={semContratoLoading}
                    handleClienteCsv={handleClienteCsv}
                    handleFuncionarioCsv={handleFuncionarioCsv}
                    handleSemContratoCsv={handleSemContratoCsv}
                />
            </PageContent>
        </Main>
    )
}